import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { card, h1, currency, fmtDate } from "./ui";
import { CalendarDays, Wallet, Clock, Users, CircleDollarSign, Inbox, ArrowUpRight, CalendarClock, FileText } from "lucide-react";

export default function Overview() {
  const [s, setS] = useState(null);
  const [docAlerts, setDocAlerts] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setS(data)).catch(() => {});
    api.get("/documents/alerts").then(({ data }) => setDocAlerts(data)).catch(() => {});
  }, []);

  const stats = s
    ? [
        { label: "Sessões nesta semana", value: s.week_count, icon: CalendarDays, testid: "stat-week" },
        { label: "A receber (sessões passadas)", value: currency(s.pending), icon: Wallet, testid: "stat-pending" },
        { label: "Previsto (sessões futuras)", value: currency(s.forecast), icon: CalendarClock, testid: "stat-forecast" },
        { label: "Recebido", value: currency(s.received), icon: CircleDollarSign, testid: "stat-received" },
        { label: "Sessões realizadas", value: s.realized_count, icon: Clock, testid: "stat-realized" },
        { label: "Pacientes", value: s.patients_count, icon: Users, testid: "stat-patients" },
        { label: "Mensagens não lidas", value: s.unread_messages, icon: Inbox, testid: "stat-unread" },
      ]
    : [];

  return (
    <div data-testid="overview-page">
      <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Painel</p>
      <h1 className={h1}>Bom te ver por aqui 🌿</h1>
      <p className="font-body text-[#4A5D4E] mt-2">Um resumo da sua rotina de atendimentos.</p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((st, i) => (
          <motion.div
            key={st.label}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.06 }}
            data-testid={st.testid}
            className={`${card} p-6`}
          >
            <div className="grid place-items-center w-11 h-11 rounded-full bg-[#F5F0E6] mb-4">
              <st.icon className="w-5 h-5 text-[#5B6E5B]" strokeWidth={1.5} />
            </div>
            <p className="font-display text-3xl text-[#2C332A]">{st.value}</p>
            <p className="font-body text-sm text-[#7A8A7A] mt-1">{st.label}</p>
          </motion.div>
        ))}
      </div>

      {docAlerts.length > 0 && (
        <div className={`${card} p-5 mt-6 border-l-4 border-l-[#C87A5E]`} data-testid="doc-alerts">
          <p className="font-display text-lg text-[#2C332A] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#C87A5E]" /> Documento informativo pendente
          </p>
          <div className="mt-2 space-y-1.5">
            {docAlerts.map((a) => (
              <p key={a.patient_id} className="font-body text-sm text-[#4A5D4E]">
                Primeira sessão de <strong>{a.patient_name}</strong> {a.days >= 0 ? `em ${fmtDate(a.date)}` : `foi em ${fmtDate(a.date)}`} e o documento ainda não consta como enviado.{" "}
                <Link to="/dashboard/pacientes" className="text-[#5B6E5B] underline underline-offset-2 hover:text-[#2C332A]">Preparar envio</Link>
              </p>
            ))}
          </div>
        </div>
      )}

      <div className={`${card} p-6 sm:p-8 mt-8`}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl text-[#2C332A]">Próximas sessões</h2>
          <Link to="/dashboard/agenda" className="inline-flex items-center gap-1 text-sm text-[#5B6E5B] hover:text-[#2C332A] transition-colors">
            Ver agenda <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        {s && s.upcoming.length === 0 && (
          <p className="font-body text-sm text-[#7A8A7A]">Nenhuma sessão agendada. Que tal abrir alguns horários?</p>
        )}
        <div className="divide-y divide-[#5B6E5B]/10">
          {s?.upcoming.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-3" data-testid="upcoming-item">
              <div>
                <p className="font-body text-[#2C332A]">{u.patient_name}</p>
                <p className="font-body text-sm text-[#7A8A7A]">{fmtDate(u.date)} · {u.time}</p>
              </div>
              <span className="font-body text-sm text-[#4A5D4E]">{currency(u.price)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
